import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
    AlertOctagon, AlertTriangle, CheckCircle2, Inbox, XCircle,
    RefreshCw, Search, MapPin, ChevronDown, ExternalLink,
    Loader2, Clock, Activity, Building2, FilterX, Layers, Timer
} from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
const AUTO_REFRESH_MS = 5 * 60 * 1000;

const TYPE_META = {
    aksioma03: { label: 'Аксиома-03', badge: 'bg-purple-500/15 text-purple-300 border-purple-500/30', dot: 'bg-purple-400' },
    aksioma01: { label: 'Аксиома-01', badge: 'bg-blue-500/15 text-blue-300 border-blue-500/30', dot: 'bg-blue-400' },
    karat307: { label: 'Карат-307', badge: 'bg-green-500/15 text-green-300 border-green-500/30', dot: 'bg-green-400' },
};

const STATUS_META = {
    alarm: { label: 'Авария', icon: AlertOctagon, iconCls: 'text-red-400', row: 'border-red-500/40 bg-red-500/5', accent: 'border-l-red-500' },
    warning: { label: 'Предупреждение', icon: AlertTriangle, iconCls: 'text-yellow-400', row: 'border-yellow-500/40 bg-yellow-500/5', accent: 'border-l-yellow-500' },
    ok: { label: 'Без замечаний', icon: CheckCircle2, iconCls: 'text-green-400', row: 'border-slate-700 bg-slate-800', accent: 'border-l-green-500/70' },
    nodata: { label: 'Нет данных', icon: Inbox, iconCls: 'text-slate-500', row: 'border-slate-700/70 bg-slate-800/50', accent: 'border-l-slate-600' },
    fetchError: { label: 'Ошибка опроса', icon: XCircle, iconCls: 'text-slate-500', row: 'border-slate-700/70 bg-slate-800/50', accent: 'border-l-slate-600' },
};

const addressOf = (r) => {
    const parts = [];
    if (r.city) parts.push(r.city);
    if (r.street) parts.push(r.street);
    if (r.houseNumber) parts.push(`дом ${r.houseNumber}`);
    return parts.length ? parts.join(', ') : 'Адрес не указан';
};

const formatNum = (val, decimals = 3) => {
    if (val === null || val === undefined) return '—';
    return Number(val).toFixed(decimals).replace('.', ',');
};

const AlarmsTab = () => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastScan, setLastScan] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(false);

    const [expandedKey, setExpandedKey] = useState(null);
    const [severityFilter, setSeverityFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [search, setSearch] = useState('');

    const loadAlarms = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/ns/alarms`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setResults((response.data || []).map(r => ({ ...r, key: `${r.typeKey}/${r.name}` })));
            setLastScan(new Date());
        } catch (err) {
            console.error('Ошибка загрузки аварий:', err);
            setError(err.response?.data?.message || 'Не удалось загрузить нештатные ситуации');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAlarms(); }, [loadAlarms]);

    useEffect(() => {
        if (!autoRefresh || loading) return;
        const id = setTimeout(() => loadAlarms(), AUTO_REFRESH_MS);
        return () => clearTimeout(id);
    }, [autoRefresh, loading, lastScan, loadAlarms]);

    // ============================================
    // ✅ Переход к устройству (паттерн из MapPage)
    // ============================================
    const goToDevice = (r) => {
        const device = {
            name: r.name,
            city: r.city,
            street: r.street,
            houseNumber: r.houseNumber,
            deviceType: r.deviceType,
            status: 1,
            fullName: r.name,
        };
        localStorage.setItem('switchPage', 'devices');
        localStorage.setItem('selectedDeviceFromMap', JSON.stringify(device));
        window.dispatchEvent(new CustomEvent('switchPageRequest'));
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('selectDeviceFromMap', { detail: { device } }));
        }, 100);
    };

    // ============================================
    // Сводка и фильтры
    // ============================================
    const summary = useMemo(() => {
        const s = { total: results.length, alarm: 0, warning: 0, ok: 0, nodata: 0, fetchError: 0, errors: 0, warnings: 0 };
        results.forEach(r => {
            s[r.status] = (s[r.status] || 0) + 1;
            s.errors += r.errorCount || 0;
            s.warnings += r.warningCount || 0;
        });
        return s;
    }, [results]);

    const filtered = useMemo(() => results.filter(r => {
        if (severityFilter !== 'all' && r.status !== severityFilter) return false;
        if (typeFilter !== 'all' && r.typeKey !== typeFilter) return false;
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            const hay = `${r.name} ${addressOf(r)}`.toLowerCase();
            if (!hay.includes(q)) return false;
        }
        return true;
    }), [results, severityFilter, typeFilter, search]);

    const typeCounts = useMemo(() => {
        const c = { aksioma03: 0, aksioma01: 0, karat307: 0 };
        results.forEach(r => { c[r.typeKey] = (c[r.typeKey] || 0) + 1; });
        return c;
    }, [results]);

    const hasActiveFilters = severityFilter !== 'all' || typeFilter !== 'all' || search.trim() !== '';

    const StatBlock = ({ id, label, value, sub, icon: Icon, cls, activeCls }) => (
        <button
            onClick={() => setSeverityFilter(severityFilter === id ? 'all' : id)}
            className={`text-left rounded-lg border px-3 py-2.5 transition ${severityFilter === id ? activeCls : 'bg-slate-900/60 border-slate-700 hover:border-slate-500'}`}
        >
            <div className="flex items-center gap-1.5 mb-1">
                <Icon className={`w-3.5 h-3.5 ${cls}`} />
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
            </div>
            <div className={`text-2xl font-bold leading-none font-mono ${cls}`}>
                {loading && results.length === 0 ? '—' : value}
            </div>
            {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
        </button>
    );

    // ============================================
    // Блок НС-сообщений
    // ============================================
    const NSMessages = ({ errors, warnings, errorCount, warningCount }) => (
        <>
            {errors?.length > 0 && (
                <div>
                    <div className="text-red-400 text-xs font-semibold mb-2 uppercase tracking-wide flex items-center gap-1.5">
                        <AlertOctagon className="w-3.5 h-3.5" /> Аварии ({errorCount})
                    </div>
                    <div className="space-y-1">
                        {errors.map((m, i) => (
                            <div key={`e-${i}`} className="flex items-start gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded text-sm">
                                <AlertOctagon className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                                <span className="text-red-200 flex-1">{m.text}</span>
                                {m.count > 1 && (
                                    <span className="px-1.5 py-0.5 rounded bg-red-500/25 text-red-300 text-xs font-mono font-bold flex-shrink-0">×{m.count}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {warnings?.length > 0 && (
                <div>
                    <div className="text-yellow-400 text-xs font-semibold mb-2 uppercase tracking-wide flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" /> Предупреждения ({warningCount})
                    </div>
                    <div className="space-y-1">
                        {warnings.map((m, i) => (
                            <div key={`w-${i}`} className="flex items-start gap-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-sm">
                                <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                                <span className="text-yellow-200 flex-1">{m.text}</span>
                                {m.count > 1 && (
                                    <span className="px-1.5 py-0.5 rounded bg-yellow-500/25 text-yellow-300 text-xs font-mono font-bold flex-shrink-0">×{m.count}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );

    // ============================================
    // Строка устройства
    // ============================================
    const NSRow = ({ r, idx }) => {
        const meta = STATUS_META[r.status] || STATUS_META.nodata;
        const tMeta = TYPE_META[r.typeKey] || TYPE_META.aksioma03;
        const Icon = meta.icon;
        const isExpanded = expandedKey === r.key;

        return (
            <div
                className={`alarm-row-in rounded-lg border border-l-4 overflow-hidden transition-shadow hover:shadow-lg hover:shadow-black/30 ${meta.row} ${meta.accent}`}
                style={{ animationDelay: `${Math.min(idx * 35, 350)}ms` }}
            >
                <div
                    className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
                    onClick={() => setExpandedKey(isExpanded ? null : r.key)}
                >
                    <div className="relative flex-shrink-0">
                        <Icon className={`w-5 h-5 ${meta.iconCls}`} />
                        {r.status === 'alarm' && (
                            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 alarm-pulse"></span>
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            {/* ✅ Переход к устройству */}
                            <button
                                onClick={(e) => { e.stopPropagation(); goToDevice(r); }}
                                className="group inline-flex items-center gap-1.5 text-white font-semibold text-sm hover:text-blue-300 transition truncate"
                                title="Перейти к устройству"
                            >
                                <span className="truncate">{r.name}</span>
                                <ExternalLink className="w-3.5 h-3.5 text-slate-500 group-hover:text-blue-400 transition flex-shrink-0" />
                            </button>
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[10px] font-semibold flex-shrink-0 ${tMeta.badge}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${tMeta.dot}`}></span>
                                {tMeta.label}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-1 text-xs text-slate-400">
                            <span className="inline-flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-slate-500 flex-shrink-0" />
                                {addressOf(r)}
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <Clock className="w-3 h-3 text-slate-500 flex-shrink-0" />
                                архив за {r.date}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        {r.status === 'alarm' && (
                            <span className="px-2.5 py-1 rounded-md bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold font-mono whitespace-nowrap">
                                Аварии: {r.errorCount}
                            </span>
                        )}
                        {(r.status === 'alarm' || r.status === 'warning') && r.warningCount > 0 && (
                            <span className="px-2.5 py-1 rounded-md bg-yellow-500/15 border border-yellow-500/40 text-yellow-300 text-xs font-bold font-mono whitespace-nowrap">
                                Предупр.: {r.warningCount}
                            </span>
                        )}
                        {r.status === 'ok' && (
                            <span className="text-green-400/80 text-xs font-medium whitespace-nowrap hidden sm:inline">Без замечаний</span>
                        )}
                        {r.status === 'nodata' && (
                            <span className="text-slate-500 text-xs whitespace-nowrap hidden sm:inline">Нет записей</span>
                        )}
                        {r.status === 'fetchError' && (
                            <span className="text-slate-500 text-xs whitespace-nowrap hidden sm:inline" title={r.errorMessage || ''}>
                                Ошибка опроса
                            </span>
                        )}
                        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                </div>

                {/* ============================================ */}
                {/* Раскрытая детализация */}
                {/* ============================================ */}
                {isExpanded && (
                    <div className="border-t border-slate-700/70 bg-slate-950/70 px-4 py-3 space-y-3">
                        {r.status === 'nodata' && (
                            <p className="text-slate-500 text-sm">В суточном архиве за {r.date} записей не найдено.</p>
                        )}
                        {r.status === 'fetchError' && (
                            <p className="text-slate-400 text-sm">
                                Не удалось прочитать архив устройства{r.errorMessage ? `: ${r.errorMessage}` : '.'}
                            </p>
                        )}

                        {/* ✅ Аксиома-03: подсистемы */}
                        {r.podSystems?.length > 0 && (
                            <div className="space-y-2">
                                {r.podSystems.map((ps, psIdx) => (
                                    <div key={psIdx} className="border border-slate-700 rounded-lg overflow-hidden">
                                        <div className="px-3 py-2 bg-slate-800/80 flex flex-wrap items-center justify-between gap-2">
                                            <span className="text-purple-300 text-xs font-semibold flex items-center gap-1.5">
                                                <Layers className="w-3.5 h-3.5" />
                                                {ps.name || `Подсистема ${psIdx + 1}`}
                                            </span>
                                            <div className="flex items-center gap-3 text-[11px] text-slate-400">
                                                {ps.timeWork != null && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <Timer className="w-3 h-3 text-green-400" />
                                                        Наработка: <span className="text-white font-mono">{formatNum(ps.timeWork, 1)}</span> час
                                                    </span>
                                                )}
                                                {ps.timeNotWork != null && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <Timer className="w-3 h-3 text-red-400" />
                                                        Простой: <span className="text-white font-mono">{formatNum(ps.timeNotWork, 1)}</span> час
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {(ps.errorCount > 0 || ps.warningCount > 0) ? (
                                            <div className="px-3 py-2 space-y-2">
                                                <NSMessages
                                                    errors={ps.errors}
                                                    warnings={ps.warnings}
                                                    errorCount={ps.errorCount}
                                                    warningCount={ps.warningCount}
                                                />
                                            </div>
                                        ) : (
                                            <div className="px-3 py-2 text-green-400/70 text-xs flex items-center gap-1.5">
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Без замечаний
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* ✅ Аксиома-01 / Карат-307: НС (без подсистем) */}
                        {!r.podSystems && (
                            <NSMessages
                                errors={r.errors}
                                warnings={r.warnings}
                                errorCount={r.errorCount}
                                warningCount={r.warningCount}
                            />
                        )}

                        {/* ✅ Наработка / простой (Аксиома-01, Карат-307) */}
                        {r.timeParams?.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-1">
                                {r.timeParams.map((tp, i) => (
                                    <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800/80 border border-slate-600 text-xs text-slate-300">
                                        <Timer className="w-3 h-3 text-blue-400" />
                                        {tp.name}: <span className="text-white font-mono font-semibold">{formatNum(tp.value)}</span>
                                        <span className="text-slate-500">{tp.unit}</span>
                                    </span>
                                ))}
                            </div>
                        )}

                        {(r.status === 'alarm' || r.status === 'warning' || r.status === 'ok') && (
                            <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                                <Activity className="w-3 h-3" />
                                Записей в суточном архиве: {r.recordsCount}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // ============================================
    // Разметка
    // ============================================
    return (
        <div className="space-y-4">
            <style>{`
                @keyframes alarmRowIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
                .alarm-row-in { animation: alarmRowIn .4s ease both; }
                @keyframes alarmPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,.5); } 50% { box-shadow: 0 0 0 6px rgba(239,68,68,0); } }
                .alarm-pulse { animation: alarmPulse 1.5s ease-in-out infinite; }
            `}</style>

            {/* Шапка */}
            <div className="relative overflow-hidden rounded-xl border border-slate-700 bg-slate-800">
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: 'radial-gradient(640px 220px at 88% -30%, rgba(249,115,22,0.14), transparent 70%), radial-gradient(520px 240px at -5% 130%, rgba(239,68,68,0.10), transparent 70%)'
                    }}
                />
                <div className="relative px-4 sm:px-5 pt-5 pb-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                                <AlertOctagon className="w-6 h-6 text-orange-400" />
                            </div>
                            <div>
                                <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight leading-tight">
                                    Аварии и предупреждения узлов учета
                                </h2>
                                <p className="text-slate-400 text-xs mt-0.5">
                                    Суточный архив: Аксиома — сегодня, Карат-307 — вчера
                                    {lastScan && !loading && <> • Обновлено: {lastScan.toLocaleTimeString('ru-RU')}</>}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setAutoRefresh(prev => !prev)}
                                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition ${autoRefresh
                                    ? 'bg-green-600/20 border-green-500/40 text-green-300'
                                    : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-700'
                                    }`}
                                title="Автоматическое обновление каждые 5 минут"
                            >
                                <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? 'bg-green-400 animate-pulse' : 'bg-slate-500'}`}></span>
                                Авто (5 мин)
                            </button>
                            <button
                                onClick={loadAlarms}
                                disabled={loading}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold transition"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                                {loading ? 'Опрос...' : 'Обновить'}
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2">
                        <StatBlock id="all" label="На обслуживании" value={summary.total} icon={Activity} cls="text-white" activeCls="bg-slate-700 border-slate-500" />
                        <StatBlock id="alarm" label="Аварии" value={summary.alarm} sub={summary.errors > 0 ? `${summary.errors} событий` : null} icon={AlertOctagon} cls="text-red-400" activeCls="bg-red-500/15 border-red-500/60" />
                        <StatBlock id="warning" label="Предупреждения" value={summary.warning} sub={summary.warnings > 0 ? `${summary.warnings} событий` : null} icon={AlertTriangle} cls="text-yellow-400" activeCls="bg-yellow-500/15 border-yellow-500/60" />
                        <StatBlock id="ok" label="Без замечаний" value={summary.ok} icon={CheckCircle2} cls="text-green-400" activeCls="bg-green-500/15 border-green-500/60" />
                        <StatBlock id="nodata" label="Нет данных" value={summary.nodata + summary.fetchError} icon={Inbox} cls="text-slate-400" activeCls="bg-slate-700 border-slate-500" />
                    </div>
                </div>

                {loading && (
                    <div className="relative">
                        <div className="flex items-center gap-1.5 px-4 sm:px-5 py-1.5 text-[11px] text-slate-400 bg-slate-900/60">
                            <Loader2 className="w-3 h-3 text-orange-400 animate-spin" />
                            Сервер опрашивает суточные архивы устройств...
                        </div>
                        <div className="h-1 bg-slate-700 overflow-hidden">
                            <div className="h-full w-1/3 bg-gradient-to-r from-orange-500 to-red-500 indeterminate-bar"></div>
                        </div>
                        <style>{`
                            .indeterminate-bar { animation: indeterminate 1.2s ease-in-out infinite; }
                            @keyframes indeterminate { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }
                        `}</style>
                    </div>
                )}
            </div>

            {error && (
                <div className="bg-red-500/15 border border-red-500/40 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 flex-shrink-0" /> {error}
                    </span>
                    <button onClick={loadAlarms} className="px-3 py-1.5 rounded bg-red-500/25 hover:bg-red-500/40 text-xs font-medium transition">
                        Повторить
                    </button>
                </div>
            )}

            {/* Фильтры */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Поиск по названию или адресу..."
                        className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/60 transition"
                    />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => setTypeFilter('all')}
                        className={`px-3 py-2 rounded-lg text-xs font-medium border transition ${typeFilter === 'all' ? 'bg-slate-600 border-slate-500 text-white' : 'bg-slate-800 border-slate-600 text-slate-300 hover:border-slate-500'}`}
                    >
                        Все типы
                    </button>
                    {Object.keys(TYPE_META).map(t => (
                        <button
                            key={t}
                            onClick={() => setTypeFilter(typeFilter === t ? 'all' : t)}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition ${typeFilter === t ? 'bg-slate-600 border-slate-500 text-white' : 'bg-slate-800 border-slate-600 text-slate-300 hover:border-slate-500'}`}
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${TYPE_META[t].dot}`}></span>
                            {TYPE_META[t].label}
                            {results.length > 0 && <span className="text-slate-500 font-mono">({typeCounts[t] || 0})</span>}
                        </button>
                    ))}
                    {hasActiveFilters && (
                        <button
                            onClick={() => { setSeverityFilter('all'); setTypeFilter('all'); setSearch(''); }}
                            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium bg-orange-600/20 border border-orange-500/40 text-orange-300 hover:bg-orange-600/30 transition"
                        >
                            <FilterX className="w-3.5 h-3.5" /> Сбросить
                        </button>
                    )}
                </div>
            </div>

            {/* Список */}
            {loading && results.length === 0 ? (
                <div className="space-y-2">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-4 animate-pulse" style={{ animationDelay: `${i * 120}ms` }}>
                            <div className="flex items-center gap-3">
                                <div className="w-5 h-5 rounded bg-slate-700"></div>
                                <div className="flex-1 space-y-2">
                                    <div className="h-3.5 bg-slate-700 rounded w-1/3"></div>
                                    <div className="h-3 bg-slate-700/70 rounded w-1/2"></div>
                                </div>
                                <div className="h-6 w-24 bg-slate-700 rounded-md"></div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="bg-slate-800 rounded-xl p-12 border border-slate-700 text-center">
                    <Inbox className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">
                        {results.length === 0
                            ? 'Нет устройств на обслуживании (status = 1) для проверки'
                            : 'Ничего не найдено по заданным фильтрам'}
                    </p>
                    {hasActiveFilters && (
                        <button
                            onClick={() => { setSeverityFilter('all'); setTypeFilter('all'); setSearch(''); }}
                            className="mt-4 px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-medium transition"
                        >
                            Сбросить фильтры
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="text-slate-400 text-xs flex items-center gap-1.5 px-1">
                        <Building2 className="w-3.5 h-3.5" />
                        Показано <span className="text-white font-semibold font-mono">{filtered.length}</span> из{' '}
                        <span className="text-white font-semibold font-mono">{results.length}</span> устройств
                    </div>
                    <div className="space-y-2">
                        {filtered.map((r, idx) => (
                            <NSRow key={r.key} r={r} idx={idx} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default AlarmsTab;